import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useApi } from 'service/useApi'
import { LanguageISO } from 'utils/localisation'
import { logError } from 'utils/logger'

import { CompanyDocumentTemplate } from '../domain'

type CompanyDocumentTemplateState = Record<string, CompanyDocumentTemplate[]>

type CompanyDocumentTemplatesState = {
  isLoading: boolean
  companyDocumentTemplates: CompanyDocumentTemplateState
  getFilledDocumentBlob: (id: string, language: LanguageISO) => Promise<Blob | undefined>
}

const CompanyDocumentTemplatesContext = createContext<CompanyDocumentTemplatesState | null>(null)

export const CompanyDocumentTemplatesContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [companyDocumentTemplates, setCompanyDocumentTemplates] = useState<CompanyDocumentTemplateState>({})
  const [isLoading, setIsLoading] = useState(true)
  const API = useApi()

  const categorizeTemplates = (newTemplates: CompanyDocumentTemplate[]) => {
    const categorizedTemplates = {}
    newTemplates.forEach((template) => {
      categorizedTemplates[template.group] = categorizedTemplates[template.group] || []
      categorizedTemplates[template.group].push(template)
    })
    setCompanyDocumentTemplates(categorizedTemplates)
  }

  const getFilledDocumentBlob = useCallback(
    async (id: string, language: LanguageISO) => {
      setIsLoading(true)
      try {
        const filledDocument = await API.documents.getFilledDocument(id, language)

        const blob = new Blob([filledDocument], { type: 'application/pdf' })

        return blob
      } catch (ignoreError) {
        logError('Failed to get Document for preview or download')
      } finally {
        setIsLoading(false)
      }
    },
    [API.documents]
  )

  useEffect(() => {
    const setData = async () => {
      try {
        const companyDocumentTemplatesFromService = await API.documents.getCompanyDocumentTemplates()
        categorizeTemplates(companyDocumentTemplatesFromService)
      } catch (ignoreError) {
        logError(`Failed to load Company Documents`)
      } finally {
        setIsLoading(false)
      }
    }
    setData()
  }, [])

  const providerValue = useMemo(
    () => ({
      isLoading,
      companyDocumentTemplates,
      getFilledDocumentBlob,
    }),
    [isLoading, companyDocumentTemplates, getFilledDocumentBlob]
  )

  return (
    <CompanyDocumentTemplatesContext.Provider value={providerValue}>
      {children}
    </CompanyDocumentTemplatesContext.Provider>
  )
}

export const useCompanyDocumentTemplates = (): CompanyDocumentTemplatesState => {
  const context = useContext(CompanyDocumentTemplatesContext)

  if (!context) {
    throw new Error('Must use useCompanyDocumentTemplates within a CompanyDocumentTemplatesContext')
  }

  return context
}
